import { useCallback, useMemo, useRef } from 'react';
import { hashKey, useQueryClient, type QueryKey } from '@tanstack/react-query';

export type AbortableTaxLookup = {
  /**
   * Supersedes the lookup in flight and returns the signal for the one starting now. Call from inside
   * `queryFn`, passing the key the run belongs to.
   */
  begin: (queryKey: QueryKey) => AbortSignal;
  /** Supersedes the lookup in flight without starting another — leaving the form, or clearing the rate. */
  abort: () => void;
};

/**
 * Tracks the tax lookup in flight so a superseded one can neither commit its rate nor be reused.
 *
 * Aborting alone only stops the commit. `updateTaxRate` checks its signal around the commit and passes it
 * to neither `getTaxRate` nor React Query, so the request itself keeps running and the aborted run's
 * `queryFn` stays pending until it returns. React Query does not evict a pending query — `gcTime: 0`
 * included — so without the removal here that key stays reachable, and an address returning to it is
 * deduped onto a run that will never write a rate: the totals keep the previous address's tax (or none)
 * while `isFetching` drops back to false and re-enables submit.
 *
 * A concrete path: shorten the zip by a character, then retype it while the first lookup is still open.
 *
 * Dropping the entry at the abort site keeps the two cases apart. A repeat of a **live** key still dedupes
 * onto the lookup already running — which is why the abort belongs inside `queryFn` and not in a change
 * handler, where it would cancel the only lookup that change has. A repeat of an **aborted** key finds
 * nothing to attach to and starts a fresh one.
 */
export function useAbortableTaxLookup(): AbortableTaxLookup {
  const queryClient = useQueryClient();
  const inFlightRef = useRef<{ controller: AbortController; queryKey: QueryKey } | null>(null);

  // `nextKey` is the key about to start, when there is one. A refetch of the key already in flight
  // (`staleTime: 0` means a window focus is enough) re-enters `queryFn` for the entry React Query is
  // running right now, and removing that would pull the query out from under the observer waiting on it.
  // Superseding it is still right; forgetting it is not.
  const supersede = useCallback(
    (nextKey: QueryKey | null) => {
      const inFlight = inFlightRef.current;
      if (inFlight == null) {
        return;
      }
      inFlightRef.current = null;
      inFlight.controller.abort();
      if (nextKey != null && hashKey(nextKey) === hashKey(inFlight.queryKey)) {
        return;
      }
      queryClient.removeQueries({ queryKey: inFlight.queryKey, exact: true });
    },
    [queryClient],
  );

  const begin = useCallback(
    (queryKey: QueryKey): AbortSignal => {
      supersede(queryKey);
      const controller = new AbortController();
      inFlightRef.current = { controller, queryKey };
      return controller.signal;
    },
    [supersede],
  );

  const abort = useCallback(() => supersede(null), [supersede]);

  // Memoised because callers hold it in effect dependencies: the "abort on unmount" cleanup must run on
  // unmount, and a fresh object each render would re-run that cleanup on every render instead, aborting
  // the lookup in flight.
  return useMemo(() => ({ begin, abort }), [begin, abort]);
}
